/**
 * O que cada número do relatório significa.
 *
 * Fonte única: os cards, os cabeçalhos das tabelas e a legenda leem daqui. Sem
 * isso, a mesma métrica ganharia três explicações levemente diferentes e a
 * primeira a divergir seria a que alguém acaba lendo.
 */

export interface MetricInfo {
    label: string;
    /** Uma linha, aparece no tooltip do cabeçalho. */
    short: string;
    /** O parágrafo da legenda: como se calcula e o que não é. */
    long: string;
}

export const METRICS = {
    cascades: {
        label: 'Cascatas',
        short: 'Quantas cascatas rodaram',
        long: 'Uma por bloco por pageview que entrou em waterfall. É o denominador do preenchimento.',
    },
    attempts: {
        label: 'Solicitações',
        short: 'Requisições ao GAM',
        long: 'Uma por step tentado. Uma cascata que desce cinco preços antes de vender gera cinco solicitações.',
    },
    wins: {
        label: 'Compras',
        short: 'Cascatas que acharam comprador',
        long: 'Cada cascata vende no máximo uma vez, no step em que o GAM preencheu. Conta a hora em que o anúncio foi entregue ao slot, que é o que responde "este preço acha comprador?". Em Rewarded e Interstitial isso ainda não é impressão: o anúncio é buscado antes e só vira impressão quando é exibido, então este número fica acima do que o GAM cobra.',
    },
    fill_rate: {
        label: 'Preenchimento',
        short: 'Compras ÷ cascatas',
        long: 'Que fração das cascatas vendeu alguma coisa. Só existe onde há contagem de cascatas — em consolidação antiga aparece como “—”.',
    },
    hit_rate: {
        label: 'Taxa de compra',
        short: 'Compras ÷ solicitações deste preço',
        long: 'Das vezes que este preço foi pedido, quantas venderam. É o denominador certo por step: o preenchimento usa cascatas, e a cascata só é contada na linha do primeiro preço.',
    },
    requests_per_sale: {
        label: 'Sol./venda',
        short: 'Solicitações ÷ compras',
        long: 'Quantas requisições ao GAM custou cada venda. Número alto significa cascata descendo muitos degraus antes de encontrar comprador.',
    },
    revenue_expected: {
        label: 'Receita esperada',
        short: 'O que o GAM deve confirmar',
        long: 'Sai das vendas que o beacon confirmou como exibidas, corrigidas pelo quanto o GAM pagou acima do piso nos últimos dias fechados. É o número feito para você olhar hoje, antes do GAM reportar. Erra por baixo com mais frequência que por cima — o preço do step é piso, e o comprador costuma pagar acima dele.',
    },
    ecpm_expected: {
        label: 'eCPM esperado',
        short: 'Receita esperada ÷ exibições × 1000',
        long: 'O eCPM correspondente à receita esperada. Difere do eCPM médio porque o denominador são as exibições confirmadas, não as entregas — a diferença é grande em Rewarded e Interstitial, onde o anúncio é buscado antes e nem sempre aparece.',
    },
    revenue_est: {
        label: 'Valor nominal dos pisos',
        short: 'Soma dos preços que ganharam ÷ 1000',
        long: 'A soma dos pisos que acharam comprador. Não é receita: conta a entrega do anúncio ao slot, e em Rewarded e Interstitial a entrega vira impressão menos da metade das vezes. Fica acima do real por construção. Use a Receita esperada para dinheiro e este número para enxergar o esforço da cascata.',
    },
    revenue_viewed: {
        label: 'Receita vista',
        short: 'Receita só das vendas confirmadas visíveis',
        long: 'É o piso do intervalo. O beacon de viewability se perde quando a aba fecha antes de ele sair, então isto subestima. E em linha de CPM padrão o GAM cobra na impressão renderizada, não na visível — só deal vCPM condiciona o pagamento à visualização. A receita real fica entre este número e a receita estimada.',
    },
    ecpm_avg: {
        label: 'eCPM médio',
        short: 'Média dos preços vencedores, ponderada pelas vendas',
        long: 'Quanto valeu cada mil impressões vendidas. Sozinho engana: uma cascata que vende uma vez a 800 tem eCPM ótimo e receita quase nula.',
    },
    rpm_request: {
        label: 'Receita por mil solicitações',
        short: 'Receita estimada ÷ solicitações × 1000',
        long: 'Normaliza pelo trabalho efetivamente pedido ao GAM. É o que torna comparável uma origem com 5 mil solicitações e outra com 82 mil — sem isso, o card só diria que quem rodou mais rendeu mais. Não depende do contador de cascatas.',
    },
    rpm_cascade: {
        label: 'Receita por mil cascatas',
        short: 'Receita estimada ÷ cascatas × 1000',
        long: 'O número honesto para comparar origens, porque põe o volume na conta. É por isso que ele, e não o eCPM, decide qual origem leva o selo de melhor retorno.',
    },
    viewable_rate: {
        label: 'Viewability efetivo',
        short: 'Vendas vistas ÷ vendas',
        long: 'Das cascatas que venderam, quantas o GPT confirmou como visíveis pelo evento impressionViewable. Não é o Active View do GAM: lá o denominador são as impressões mensuráveis, e aqui é o que a cascata vendeu. Beacon perdido conta contra, então este número tende a sair mais baixo. Os dois não são comparáveis.',
    },
    dead: {
        label: 'Step morto',
        short: 'Preço tentado que nunca comprou',
        long: 'Cada tentativa dele gasta uma requisição e um pedaço do timeout sem retorno. São os candidatos a sair da cascata ou a baixar de preço.',
    },
} as const satisfies Record<string, MetricInfo>;

export type MetricKey = keyof typeof METRICS;

/** Ordem em que a legenda apresenta as métricas. */
export const LEGEND_ORDER: MetricKey[] = [
    'revenue_expected',
    'ecpm_expected',
    'cascades',
    'attempts',
    'wins',
    'fill_rate',
    'hit_rate',
    'requests_per_sale',
    'revenue_est',
    'ecpm_avg',
    'rpm_cascade',
    'viewable_rate',
    'dead',
];
